// src/components/surf/DemoSurfPanel.jsx
//
// Surf for a prospect demo account (tenants.reviewer_kind = 'prospect_demo'). Unlike the normal widget this panel is ALWAYS open:
// no close button, no Escape, no backdrop. On a desktop it is docked down the right edge (Layout reserves the room); on a phone it
// is a bottom panel that stays up.
//
// It knows who the prospect is (name saved by the admin form / the /demo page) and which page they are on. Arriving on a page adds a
// pre-written line plus buttons (no AI call): one message per page, replaced when they move on, so the panel never shows a stale
// 'you are on X' line. The first one carries the greeting. Typing a question goes to /api/demo-guide, which answers with that page's facts.
// The conversation is kept in localStorage so a reload picks up where it left off.

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import { fetchWithCsrf } from '../../lib/fetchWithCsrf';
import { trackDemo } from '../../lib/demoTrack';

const MAX_SAVED = 60;
const MAX_SENT = 12;
// How long a visitor can sit still on a stop before Surf says what to do next. Once per page visit.
const IDLE_NUDGE_MS = 60000;

const load = (key) => { try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch { return null; } };
const save = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* private mode: still works, just no restore */ } };

// The mascot, large, like the customer Surf widget (SurfWidget.jsx FoxAsset): glow behind, gentle float, brighter glow while Surf answers.
const FOX_STYLES = `
@keyframes surf-fox-float { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
.surf-fox-float { animation: surf-fox-float 6s ease-in-out infinite; }
`;

function FoxStage({ busy }) {
  return (
    <div className="relative w-full h-full">
      <style>{FOX_STYLES}</style>
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-[340px] h-32 rounded-full bg-cyan-500 blur-[70px] transition-all duration-700 pointer-events-none ${busy ? 'opacity-90' : 'opacity-45'}`} />
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 w-44 h-20 rounded-full bg-cyan-300 blur-3xl transition-all duration-500 pointer-events-none ${busy ? 'opacity-80' : 'opacity-40'}`} />
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-56 h-px bg-gradient-to-r from-transparent via-cyan-300/70 to-transparent pointer-events-none" />
      <div className="absolute inset-0 flex justify-center items-end pointer-events-none">
        <img src="/full_body_sitting.png" alt="Surf" className="h-auto max-h-full w-auto max-w-full object-contain surf-fox-float" style={{ filter: 'drop-shadow(0 0 12px rgba(0, 200, 180, 0.45))' }} />
      </div>
    </div>
  );
}

export default function DemoSurfPanel({ userId }) {
  const location = useLocation();
  const navigate = useNavigate();
  const key = `surf_demo_${userId || 'x'}`;

  const [ctx, setCtx] = useState(null); // { greeting }
  const [pg, setPg] = useState(null); // { id, line, next, ask } for the current page
  const [messages, setMessages] = useState(() => (load(key)?.messages) || []);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  // Where they are in the three-stop tour: 1 Test conversation on the first campaign, 2 the hot lead handoff in Pulse, 3 the second
  // campaign, 4 = finished. A ref mirrors each value so event handlers read the latest without re-subscribing.
  const [step, setStepState] = useState(() => load(key)?.step || 1);
  const [pulseSeen, setPulseSeenState] = useState(() => !!load(key)?.pulseSeen);
  const stepRef = useRef(step);
  const pulseSeenRef = useRef(pulseSeen);
  const setStep = (n) => { stepRef.current = n; setStepState(n); };
  const setPulseSeen = (b) => { pulseSeenRef.current = b; setPulseSeenState(b); };
  // While the intro video is playing the panel says nothing: no greeting, no stop line, no reminder. It all starts at Continue.
  const [introOpen, setIntroOpen] = useState(() => !!window.__sfxIntroOpen);
  useEffect(() => {
    const on = (e) => setIntroOpen(!!e.detail?.open);
    window.addEventListener('sfx:intro-state', on);
    // The video component sits before this panel in the page, so its effect runs first and has usually ALREADY announced itself by now:
    // the state above was read before that happened. Read the flag again now that we are listening, or the panel talks over the video.
    setIntroOpen(!!window.__sfxIntroOpen);
    return () => window.removeEventListener('sfx:intro-state', on);
  }, []);
  const testOpenedAt = useRef(0); // when the Test conversation window opened, for how long they stayed
  const nudgedFor = useRef(null); // the page id the idle nudge already fired on
  const pgRef = useRef(null);
  const pathRef = useRef(location.pathname);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Who the prospect is. Failure is fine: Surf just says "Hi" without a name.
  useEffect(() => {
    let live = true;
    fetchWithCsrf('/api/demo-guide/context').then((r) => (r.ok ? r.json() : null)).then((d) => { if (live) setCtx(d || {}); }).catch(() => { if (live) setCtx({}); });
    return () => { live = false; };
  }, []);

  useEffect(() => { save(key, { messages: messages.slice(-MAX_SAVED), step, pulseSeen }); }, [key, messages, step, pulseSeen]);
  useEffect(() => { pgRef.current = pg; }, [pg]);
  // What they do in the demo, for Tom (src/lib/demoActivity.js): every page they land on, and how far through the tour they got.
  useEffect(() => { trackDemo({ type: 'page_view', path: location.pathname }); }, [location.pathname]);
  useEffect(() => { trackDemo({ type: 'tour_step', step }); }, [step]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  // Windows such as Test conversation tell Surf they opened: window.dispatchEvent(new CustomEvent('surf:demo-context', { detail: { modal: 'test_conversation' } })).
  useEffect(() => {
    const on = (e) => {
      const id = e.detail?.modal || null;
      setModal(id);
      if (id === 'test_conversation') { testOpenedAt.current = Date.now(); trackDemo({ type: 'test_open' }); }
      if (!id && testOpenedAt.current) { trackDemo({ type: 'test_close', seconds: (Date.now() - testOpenedAt.current) / 1000 }); testOpenedAt.current = 0; }
      if (!id) {
        // The Test conversation window closed. On stop 1 or 3 that completes the stop: move on and say what is next.
        const cur = stepRef.current;
        if (cur === 1 || cur === 3) {
          const nextStep = cur + 1;
          setStep(nextStep);
          fetchWithCsrf(`/api/demo-guide/page?path=${encodeURIComponent(pathRef.current)}&step=${nextStep}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => {
              if (!d) return;
              setPg(d);
              setMessages((m) => [...m.filter((x) => !x.page && !x.nudge), { role: 'assistant', text: d.done ? (d.finish || d.line) : `Nice work. ${d.nudge}`, page: d.id }]);
            })
            .catch(() => {});
        }
        return;
      }
      fetchWithCsrf(`/api/demo-guide/modal?id=${encodeURIComponent(id)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d?.line) setMessages((m) => [...m, { role: 'assistant', text: d.line }]); })
        .catch(() => {});
    };
    window.addEventListener('surf:demo-context', on);
    return () => window.removeEventListener('surf:demo-context', on);
  }, []);

  // The page text comes from the server (one copy, src/lib/demoGuide.js). One arrival message per page: the greeting and the page
  // line share a bubble the first time, and moving to another page REPLACES the earlier page message (typed questions and Surf's
  // answers stay). Reloading on the same page adds nothing.
  const arrive = useCallback((path, isLive = () => true) => {
    if (!ctx) return;
    fetchWithCsrf(`/api/demo-guide/page?path=${encodeURIComponent(path)}&step=${stepRef.current}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!isLive() || !d) return;
        setPg(d);
        setMessages((m) => {
          const lastPage = [...m].reverse().find((x) => x.page);
          if (lastPage && lastPage.page === d.id) return m;
          const first = m.length === 0 && ctx.greeting;
          return [...m.filter((x) => !x.page && !x.nudge), { role: 'assistant', text: first ? `${ctx.greeting}\n\n${d.line}` : d.line, page: d.id }];
        });
      })
      .catch(() => {});
  }, [ctx]);

  useEffect(() => {
    if (!ctx || introOpen) return undefined;
    let live = true;
    pathRef.current = location.pathname;
    // Tour bookkeeping BEFORE asking what to say: seeing Pulse on stop 2 counts; coming back to Campaigns after that is stop 3.
    if (stepRef.current === 2 && location.pathname === '/control-room') setPulseSeen(true);
    if (stepRef.current === 2 && pulseSeenRef.current && location.pathname === '/campaign-management') setStep(3);
    arrive(location.pathname, () => live);
    return () => { live = false; };
  }, [ctx, introOpen, location.pathname, arrive]);

  // Sitting still on a stop for a while: say what to do here, once. Any typed question or move to another page resets it.
  useEffect(() => {
    if (introOpen || !pg || !pg.nudge || nudgedFor.current === pg.id) return undefined;
    const t = setTimeout(() => {
      nudgedFor.current = pg.id;
      setMessages((m) => [...m.filter((x) => !x.nudge), { role: 'assistant', text: pg.nudge, nudge: true }]);
    }, IDLE_NUDGE_MS);
    return () => clearTimeout(t);
  }, [pg, messages.length, introOpen]);


  async function ask(text) {
    const q = String(text || '').trim();
    if (!q || busy) return;
    const history = messages.filter((m) => m.role === 'user' || m.role === 'assistant').slice(-MAX_SENT).map((m) => ({ role: m.role, content: m.text }));
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setBusy(true);
    try {
      const res = await fetchWithCsrf('/api/demo-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q, history, path: location.pathname, modal, step }),
      });
      const data = res.ok ? await res.json() : null;
      setMessages((m) => [...m, { role: 'assistant', text: data?.message || 'Surf lost the connection. Try again in a sec?' }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: 'Surf lost the connection. Try again in a sec?' }]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  const tour = ctx?.tour || [];
  const chips = pg ? [
    ...(pg.next ? [{ label: pg.next.label, go: pg.next.path, primary: true }] : []),
    ...(pg.ask || []).map((q) => ({ label: q, q })),
  ] : [];

  // Drawn straight onto the page body, above everything, like the Test conversation window it has to stay beside: that window is
  // a body-level overlay with a dark backdrop, and a panel left inside the app layout can end up underneath it (greyed out).
  return createPortal(
    <div
      data-testid="demo-surf-panel"
      className="fixed z-[2147483000] bg-slate-950 border-cyan-500/30 shadow-2xl shadow-cyan-500/20 flex flex-col overflow-hidden
                 left-0 right-0 bottom-0 h-[42vh] border-t
                 lg:left-auto lg:top-0 lg:h-auto lg:w-[380px] lg:border-t-0 lg:border-l"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-800/80 bg-slate-950">
        <img src="/full_body_sitting.png" alt="" className="h-9 w-auto lg:hidden" style={{ filter: 'drop-shadow(0 0 8px rgba(0,200,180,0.45))' }} />
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400/80 font-medium">Surf</div>
          <div className="text-[11px] text-slate-400">Your guide to this demo</div>
        </div>
        <div className={`ml-auto w-2 h-2 rounded-full ${busy ? 'bg-cyan-300' : 'bg-cyan-400 animate-pulse'}`} />
      </div>

      {tour.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-800/80 bg-slate-950 flex items-center gap-2" data-testid="tour-progress">
          <div className="text-[11px] text-slate-400 whitespace-nowrap">{step > tour.length ? `All ${tour.length} stops done` : `Stop ${step} of ${tour.length}: ${tour[step - 1]?.title || ''}`}</div>
          <div className="flex gap-1 ml-auto">
            {tour.map((t) => (
              <span key={t.stop} title={t.title} className={`h-1.5 w-6 rounded-full ${step === t.stop ? 'bg-cyan-300' : step > t.stop ? 'bg-cyan-500/60' : 'bg-slate-700'}`} />
            ))}
          </div>
        </div>
      )}

      {/* Desktop: the big mascot (the phone panel is too short for it and keeps the small one in the header). */}
      <div className="hidden lg:block relative h-[36%] min-h-[260px] flex-shrink-0 overflow-hidden border-b border-slate-800/60">
        <FoxStage busy={busy} />
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto bg-slate-900 px-4 py-3 space-y-3 text-sm">
        {messages.map((m, i) => (m.role === 'assistant' ? (
          <div key={i} className="max-w-[92%] rounded-2xl rounded-tl-sm bg-slate-800/80 border border-slate-700/50 px-3.5 py-2.5 text-slate-100 leading-relaxed whitespace-pre-line">{m.text}</div>
        ) : (
          <div key={i} className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-cyan-500/15 border border-cyan-500/30 px-3.5 py-2.5 text-slate-100 leading-relaxed">{m.text}</div>
          </div>
        )))}
        {busy && <div className="text-slate-500 text-xs">Surf is typing...</div>}
      </div>

      <div className="bg-slate-900 border-t border-slate-800 px-3 pt-2 pb-3">
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {chips.map((c) => (
              <button
                key={c.label}
                type="button"
                disabled={busy}
                onClick={() => (c.go ? navigate(c.go) : ask(c.q))}
                className={`text-xs rounded-full px-2.5 py-1 disabled:opacity-50 ${c.primary ? 'bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300' : 'border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 focus-within:border-cyan-500/60 rounded-lg px-3 py-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Surf about this page..."
            className="flex-1 bg-transparent text-slate-100 placeholder-slate-500 text-sm focus:outline-none"
            autoComplete="off"
            disabled={busy}
          />
          <button type="submit" disabled={!input.trim() || busy} className="p-1.5 rounded bg-cyan-500 text-slate-950 hover:bg-cyan-400 disabled:bg-slate-700 disabled:text-slate-500" aria-label="Send">
            <Send size={14} />
          </button>
        </form>
      </div>
    </div>,
    document.body,
  );
}
